import { fastHash } from '../utils/helpers';

export type PodcastId = `${number}`;

export type Podcast = {
  id: PodcastId;
  name: string;
  slug: string;
  artistName: string;
  feedUrl: string;
  images: Array<{ url: string; size: number }>;
  episodeCount: number;
  description?: string;
  episodes: Episode[];
  optOutReason?: string;
  language?: string;
};

export type PodcastOnly = Omit<Podcast, 'episodes'>;

export type SerializedPodcast = PodcastOnly & { episodes: SerializedEpisode[] };

export type Ad = {
  startTime: number;
  endTime: number;
};

// PodcastId|EpisodeGuidHash
export type EpisodeCompoundId = `${PodcastId}|${string}`;

export type EpisodeStatus =
  | 'pending'
  | 'detecting_ads'
  | 'done'
  | 'audio_download_failed'
  | 'ad_detection_failed';

// Episode from the API
export type EpisodeFromApi = {
  title: string;
  pubDate: string;
  guidHash: string;
  filename: string;
  filesize: number;
  // API will always provide an excerpt, and may provide a full description
  excerpt: string;
  description?: string;
  ads: Ad[] | null;
  duration?: number;
  isSaved: boolean;
  // Derived from Last-Modified header
  queriedAt: number;
  // Provided ONLY for episodes that we don't allow ad-skipping on
  url?: string;
  season?: number;
  status?: EpisodeStatus;
  omit?: true;
};

// Episode stored in LocalStorage / IndexedDB
export type SerializedEpisode = {
  title: string;
  pubDate: string;
  guidHash: string;
  filename: string;
  filesize: number;
  // LocalStorage / IndexedDB will only store excerpt for fast retrieval
  excerpt?: string;
  ads: Ad[] | null;
  duration?: number;
  isSaved: boolean;
  queriedAt: number;
  url?: string;
  season?: number;
  omit?: true;
  // We don't store status in LocalStorage since it may change w/ time
};

export class Episode {
  readonly title!: string;
  readonly pubDate!: Date;
  readonly guidHash!: string;
  readonly filename!: string;
  readonly filesize!: number;
  readonly excerpt!: string | undefined;
  readonly ads!: Ad[] | null;
  readonly duration: number | undefined;
  readonly isSaved!: boolean;
  readonly queriedAt!: number;
  readonly podcast!: Podcast;
  readonly url?: string;
  readonly season?: number;
  readonly status: EpisodeStatus;
  readonly omit: boolean;
  readonly contentHash: string;
  _hash: string;

  constructor(
    episode: SerializedEpisode | EpisodeFromApi,
    podcast: Podcast | SerializedPodcast
  ) {
    if (!podcast) {
      console.log('episode', episode);
      throw new Error('Missing podcast for Episode');
    }

    this.title = episode.title;
    this.pubDate = new Date(episode.pubDate);
    this.guidHash = episode.guidHash;
    this.filename = episode.filename;
    this.filesize = episode.filesize;
    this.excerpt = episode.excerpt;
    this.ads = episode.ads;
    this.duration = episode.duration;
    this.isSaved = episode.isSaved;
    this.queriedAt = episode.queriedAt;
    this.url = episode.url;
    this.season = episode.season;
    this.status =
      ('status' in episode && episode.status) || (episode.ads ? 'done' : 'pending');
    this.omit = episode.omit === true;

    // TEMP: Ensure that episode duration is a number (it used to be a string in the database
    // and is probably still stored that way in LocalStorage)
    if (typeof this.duration === 'string') {
      this.duration = parseFloat(this.duration) || undefined;
    }

    // Add podcast property but don't save it to JSON
    Object.defineProperty(this, 'podcast', {
      get() {
        return podcast;
      },
      enumerable: false,
    });
  }
  get podcastId() {
    return this.podcast.id;
  }
  get id(): EpisodeCompoundId {
    return `${this.podcast.id}|${this.guidHash}`;
  }
  get audioUrl() {
    // Use custom URL if provided
    if (this.url) return this.url;

    const baseUrl = 'https://podcasts.adblockpodcast.com/file/adp-podcast-mp3s';
    return `${baseUrl}/${this.podcast.slug}-${this.podcastId}/${this.filename}`;
  }
  get serialize(): SerializedEpisode {
    return {
      ...this,
      pubDate: this.pubDate.toISOString(),
      status: undefined,
    };
  }
  get hash() {
    if (!this._hash) {
      // Compute content hash lazily w/out queriedAt. Optimized for speed!
      // Helps us avoid swapping out a bunch of episodes if the content is the same
      this._hash = fastHash(
        `${this.title}|${this.pubDate}|${this.guidHash}|${this.filename}|${this.filesize}|${JSON.stringify(this.ads)}|${this.duration}|${this.isSaved}|${this.url}|${this.season}|${this.status}|${this.omit}|${this.excerpt}`
      );
    }
    return this._hash;
  }
}
